'use client';

import React from 'react';
import { MdApps, MdOpenInNew, MdFavorite, MdFavoriteBorder, MdShare, MdSearch } from 'react-icons/md';

import { MarketplaceCategory } from '../../types';
import type { MarketplaceAppWithSecurityReport } from '../../types';

import useMarketplaceApps from 'src/hooks/useMarketplaceApps';
import useMarketplaceCategories from 'src/hooks/useMarketplaceCategories';

import MarketplaceAppModal from '../MarketplaceAppModal';
import styles from './AppsPage.module.css';

const AppsPage: React.FC = () => {
  const [ favoriteApps, setFavoriteApps ] = React.useState<Array<string>>([ ]);
  const [ selectedCategory, setSelectedCategory ] = React.useState<string>(MarketplaceCategory.ALL);
  const [ searchQuery, setSearchQuery ] = React.useState('');
  const [ selectedApp, setSelectedApp ] = React.useState<MarketplaceAppWithSecurityReport | null>(null);

  React.useEffect(() => {
    try {
      const stored = localStorage.getItem('favoriteApps');
      const apps = stored ? JSON.parse(stored) as Array<string> : [ ];
      setFavoriteApps(apps);
    } catch (e) {
      setFavoriteApps([ ]);
    }
  }, [ ]);

  const {
    data: allApps,
    displayedApps,
    isPlaceholderData,
    isError,
  } = useMarketplaceApps(searchQuery, selectedCategory, favoriteApps);

  const { data: categories, isPlaceholderData: isCategoriesLoading } = useMarketplaceCategories(allApps, isPlaceholderData);

  const apps = displayedApps || [ ];

  const handleFavoriteClick = (appId: string) => {
    const isFavorite = favoriteApps.includes(appId);
    let newFavorites: Array<string>;

    if (isFavorite) {
      newFavorites = favoriteApps.filter(id => id !== appId);
    } else {
      newFavorites = [ ...favoriteApps, appId ];
    }

    setFavoriteApps(newFavorites);
    localStorage.setItem('favoriteApps', JSON.stringify(newFavorites));
  };

  const handleShare = async(app: any) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: app.title,
          text: app.shortDescription,
          url: app.url,
        });
      } catch (e) {
        // User cancelled or error
      }
    }
  };

  return (
    <div className={ styles.page }>
      <div className={ styles.pageHeader }>
        <h1 className={ styles.pageTitle }>DAppscout</h1>
        <div className={ styles.headerActions }>
          <a href="https://opengpu.network/" target="_blank" rel="noopener noreferrer" className={ styles.headerButton }>
            Submit app ↗
          </a>
          <a href="https://opengpu.network/" target="_blank" rel="noopener noreferrer" className={ styles.headerButton }>
            Suggest ideas ↗
          </a>
        </div>
      </div>

      <div className={ styles.tabsContainer }>
        <div className={ styles.tabs }>
          <button
            className={ `${ styles.tab } ${ selectedCategory === MarketplaceCategory.FAVORITES ? styles.tabActive : '' }` }
            onClick={ () => setSelectedCategory(MarketplaceCategory.FAVORITES) }
          >
            ❤️ { favoriteApps.length }
          </button>
          <button
            className={ `${ styles.tab } ${ selectedCategory === MarketplaceCategory.ALL ? styles.tabActive : '' }` }
            onClick={ () => setSelectedCategory(MarketplaceCategory.ALL) }
          >
            All { allApps?.length || 0 }
          </button>
          { !isCategoriesLoading && categories.map((category) => (
            <button
              key={ category.name }
              className={ `${ styles.tab } ${ selectedCategory === category.name ? styles.tabActive : '' }` }
              onClick={ () => setSelectedCategory(category.name) }
            >
              { category.name } { category.count }
            </button>
          )) }
        </div>
      </div>

      <div className={ styles.searchContainer }>
        <div className={ styles.searchWrapper }>
          <MdSearch className={ styles.searchIcon } size={ 20 }/>
          <input
            type="text"
            placeholder="Find app by name or keyword..."
            value={ searchQuery }
            onChange={ (e) => setSearchQuery(e.target.value) }
            className={ styles.searchInput }
          />
        </div>
      </div>

      { isPlaceholderData && (
        <div className={ styles.grid }>
          { Array.from({ length: 6 }).map((_, i) => (
            <div key={ i } className={ styles.skeletonCard }>
              <div className={ styles.skeletonCardHeader }>
                <span className={ styles.skeletonLogo } />
                <span className={ styles.skeleton } style={{ width: '120px', height: '20px' }} />
              </div>
              <span className={ styles.skeleton } style={{ width: '80px', height: '14px' }} />
              <span className={ styles.skeleton } style={{ width: '100%', height: '14px', marginTop: '12px' }} />
              <span className={ styles.skeleton } style={{ width: '70%', height: '14px', marginTop: '6px' }} />
              <div className={ styles.skeletonFooter }>
                <span className={ styles.skeleton } style={{ width: '80px', height: '32px', borderRadius: '6px' }} />
                <div style={{ display: 'flex', gap: '8px' }}>
                  <span className={ styles.skeleton } style={{ width: '36px', height: '36px', borderRadius: '6px' }} />
                  <span className={ styles.skeleton } style={{ width: '36px', height: '36px', borderRadius: '6px' }} />
                </div>
              </div>
            </div>
          )) }
        </div>
      ) }

      { isError && (
        <div className={ styles.error }>
          <p>Failed to load applications. Please try again later.</p>
        </div>
      ) }

      { !isPlaceholderData && !isError && apps.length === 0 && (
        <div className={ styles.emptyState }>
          <MdApps size={ 64 }/>
          <h2>No DApps Available</h2>
          <p>There are currently no applications available in the marketplace.</p>
        </div>
      ) }

      { !isPlaceholderData && !isError && apps.length > 0 && (
        <div className={ styles.grid }>
          { apps.map((app) => (
            <div key={ app.id } className={ styles.appCard }>
              <div className={ styles.cardHeader }>
                <div className={ styles.appLogo }>
                  { app.logo ? (
                    <img src={ app.logo } alt={ app.title }/>
                  ) : (
                    <MdApps size={ 48 }/>
                  ) }
                </div>
                <div className={ styles.cardTitle }>
                  <h3>{ app.title }</h3>
                  { app.url && <MdOpenInNew size={ 16 }/> }
                </div>
              </div>

              { app.categories && app.categories.length > 0 && (
                <div className={ styles.category }>
                  { app.categories[0] }
                </div>
              ) }

              { app.shortDescription && (
                <p className={ styles.description }>{ app.shortDescription }</p>
              ) }

              <div className={ styles.cardFooter }>
                <button className={ styles.moreInfo } onClick={ () => setSelectedApp(app as MarketplaceAppWithSecurityReport) }>More info</button>
                <div className={ styles.actions }>
                  <button
                    className={ styles.iconButton }
                    onClick={ () => handleFavoriteClick(app.id) }
                  >
                    { favoriteApps.includes(app.id) ? (
                      <MdFavorite size={ 20 } color="#ef4444"/>
                    ) : (
                      <MdFavoriteBorder size={ 20 }/>
                    ) }
                  </button>
                  <button
                    className={ styles.iconButton }
                    onClick={ () => handleShare(app) }
                  >
                    <MdShare size={ 20 }/>
                  </button>
                </div>
              </div>
            </div>
          )) }
        </div>
      ) }

      <MarketplaceAppModal
        isOpen={ !!selectedApp }
        onClose={ () => setSelectedApp(null) }
        app={ selectedApp }
        isFavorite={ selectedApp ? favoriteApps.includes(selectedApp.id) : false }
        onFavoriteClick={ handleFavoriteClick }
      />
    </div>
  );
};

export default AppsPage;
